import moment from 'moment'
import {
  KEY_PRACTICE_AFFIRMATIONS,
  KEY_PRACTICE_BREATHWORK,
  KEY_PRACTICE_COPING_SKILLS,
  KEY_PRACTICE_JOURNALING,
  KEY_PRACTICE_MEDITATIONS,
  KEY_PRACTICE_RELATIONSHIPQA,
  KEY_PRACTICE_COMPLIMENTS,
  KEY_PRACTICE_DATE_IDEAS,
  WP_API_URL,
  WP_API_PUBLIC_URL,
  REMBLE_API_URL,
  practicesGET,
  practicesPOST,
  practicesPATCH,
  practicesDELETE,
  buildRequestURL,
  KEY_PRACTICE_JOURNALING_WP,
  KEY_PRACTICE_MEDITATIONS_WP,
  KEY_PRACTICE_BREATHWORK_WP,
  KEY_PRACTICE_COPING_SKILLS_WP,
  getRESTParams,
  REMBLE_WP_API_URL
} from '.'
import { app } from '../../../config'
import { practices } from './constants'

const PRACTICES_BASE_PATH = `${REMBLE_API_URL}/lms/practices`
const WPJSON_BASE_PATH = `${REMBLE_API_URL}/lms/wp-json`

const ERR_CODE_JWT_OBSOLETE = 'jwt_auth_obsolete_token'
const ERR_JWT_OBSOLETE =
  'Your authorization token has expired. Please logout and login again.'

/* =============================================
 * Private helpers
 * ============================================= */

// Shorthand reference for affirmation categories
const affirmationTodayCategoryId = app.practices.affirmation.today_cat_id
const affirmationStartYourDayCategoryId =
  app.practices.affirmation.start_your_day_cat_id

// Shorthand reference for practices category IDs
const journalingDailyCategoryId = app.practices.journaling.daily_cat_id
const journalingFreeEntryCategoryId = app.practices.journaling.free_entry_cat_id

const complimentsCategoryId = app.practices.compliments.parent_cat_id
const dateIdeasCategoryId = app.practices.date_ideas.parent_cat_id

/**
 * Private helper function to retrieve practice data from WP using a common pattern
 * @param {String} practiceTypeKey The practice type to retrieve data for. Should be one of the `KEY_PRACTICE` values
 * @param {Number} practiceCategoryId The WP category ID that the practice belongs to. These are defined in the .env file
 */
const _getPracticeData = async (practiceTypeKey, practiceCategoryId) => {
  const uri = `/${practiceTypeKey}/category/${practiceCategoryId}`
  const url = buildRequestURL(PRACTICES_BASE_PATH, { uri, per_page: 100 })
  return await practicesGET(url)
}

/**
 * Private helper function to retrieve practice category data from WP using a common pattern
 * @param {String} practiceTypeKey The practice type to retrieve the category list for. Should be one of the `KEY_PRACTICE` values
 * @returns {Array} The list of practice items
 */
const _getPracticeCategoryData = async practiceTypeKey => {
  const uri = `${REMBLE_WP_API_URL}/practices/categories/type/${practiceTypeKey}`
  const url = buildRequestURL(uri)
  const result = await practicesGET(url)

  // Return an empty array if there are no results
  if (!result) return []

  // Sometimes WP returns responses as arrays and sometimes as objects :facepalm:
  // so let's normalize and always return an array
  return result.length ? result : Object.values(result)
}

/**
 * Private helper function to retrieve practice parent category data from WP using a common pattern
 * @param {String} practiceTypeKey The practice type to retrieve the list for. Should be one of the `KEY_PRACTICE` values
 * @param {Number} perPage The number of items to retrieve per page
 * @param {Boolean} includeChildren Whether or not to include child categories in the results
 * @param {Boolean} includeGrandchildren Whether or not to include grandchild categories in the results
 * @returns {Array} The list of practice items
 */
const _getPracticeParentCategoryData = async (
  practiceTypeKey,
  perPage = 100,
  includeChildren = false,
  includeGrandchildren = false
) => {
  const uri = `/categories/${
    app.practices[practiceTypeKey]?.parent_cat_id || practiceTypeKey
  }`

  const url = buildRequestURL(PRACTICES_BASE_PATH, {
    uri,
    per_page: perPage,
    include_children: includeChildren,
    include_grandchildren: includeGrandchildren
  })
  const result = await practicesGET(url)

  // Return an empty array if there are no results
  if (!result) return []

  // Sometimes WP returns responses as arrays and sometimes as objects :facepalm:
  // so let's normalize and always return an array
  return result.length ? result : Object.values(result)
}

/**
 * Private helper function to retrieve practices from WP using a common pattern
 * @param {String} practiceTypeKey The practice type to retrieve the list for. Should be one of the `KEY_PRACTICE` values
 * @param {Number} perPage The number of items to retrieve per page
 * @returns {Array} The list of practice items
 */
const _getPracticesByType = async (practiceTypeKey, perPage = 100) => {
  const uri = `${WP_API_PUBLIC_URL}/${practiceTypeKey}`
  const practices = await practicesGET(WPJSON_BASE_PATH, {
    uri,
    per_page: perPage
  })

  // Return an empty array if there are no results
  if (!practices) return []

  return practices?.map(_normalizePracticeData)
}

/**
 * Normalizes wordpress data.
 * @param {Array} wordpressData The wordpress data to normalize.
 */
const _normalizePracticeData = wordpressData => ({
  id: wordpressData.id,
  categories: wordpressData.categories,
  title: wordpressData.title.rendered,
  practiceId: wordpressData.id,
  acf: wordpressData.acf,
  acf_extended: wordpressData.acf_extended,
  content: wordpressData.content,
  featured_image_thumbnail_url: wordpressData.featured_image_thumbnail_url,
  featured_image_url: wordpressData.featured_image_url
})

/**
 * Clean daily journaling data
 * @param {Object} wordpressData The wordpress data to clean.
 * @returns {Object} The cleaned data
 * */
const cleanDailyJournalingData = wordpressData => ({
  term_id: wordpressData.ID,
  name: wordpressData.post_title
})

/* =============================================
 * Public functions
 * ============================================= */

/**
 * Helper that checks to see whether a practice category is free
 * @param {Number} practiceCategoryId The category ID to check
 * @param {String} practiceType The practice type that the category belongs to (e.g., 'affirmations', 'journaling', etc.)
 * @returns Boolean value indicating whether or not the practice category is free
 */
const isFreePracticeCategory = (practiceType, practiceCategoryId) => {
  // TODO: Use a category ID in WP to determine if a practice is free
  // instead of checking the practice type and category ID

  // Set our IDs per type
  const types = {}

  // Affirmations
  types[KEY_PRACTICE_AFFIRMATIONS] = parseInt(
    app.plans.free_affirmation_category_id,
    10
  )

  // Breathwork
  types[KEY_PRACTICE_BREATHWORK] = parseInt(app.plans.free_breathwork_id, 10)

  // Journaling
  types[KEY_PRACTICE_JOURNALING] = parseInt(
    app.plans.free_journaling_category_id,
    10
  )

  // Relationship QA
  types[KEY_PRACTICE_RELATIONSHIPQA] = parseInt(
    app.plans.free_relationship_qa_category_id,
    10
  )

  // Meditations
  types[KEY_PRACTICE_MEDITATIONS] = parseInt(app.plans.free_meditation_id, 10)

  // Coping Skills
  types[KEY_PRACTICE_COPING_SKILLS] = parseInt(
    app.plans.free_coping_skill_id,
    10
  )

  // Date Ideas
  types[KEY_PRACTICE_DATE_IDEAS] = parseInt(
    app.plans.free_date_ideas_category_id,
    10
  )

  // Compliments
  types[KEY_PRACTICE_COMPLIMENTS] = parseInt(
    app.plans.free_compliments_category_id,
    10
  )

  const isFree =
    types[practiceType] && types[practiceType] === practiceCategoryId

  return !!isFree
}

/**
 * Retrieves summary data and basic info about all practices, in the future, this could be moved
 * to an API endpoint, but for now it'll stay here since it won't likely change often (famous last words lol)
 */
const getAllPracticesDetails = () => practices // Defined in constants

/**
 * Retrieves summary data and basic info about a specific practice type, in the future, this could be moved
 * to an API endpoint, but for now it'll stay here since it won't likely change often (famous last words lol)
 * @param {Object} practiceType The practice type to get information for. Should be one of `KEY_PRACTICE_` keys
 */
const getPracticeDetails = practiceType =>
  getAllPracticesDetails().find(practice => practice.key === practiceType)

/**
 * Retrieves a list of available practice categories for the given practice type
 * @param {String} practiceKey The practice type to retrieve the list for. Should be one of the `KEY_PRACTICE` values
 * @param {Number} perPage The number of items to retrieve per page
 * @param {Boolean} includeChildren Whether or not to include child categories
 * @param {Boolean} includeGrandchildren Whether or not to include grandchild categories
 * @returns {Array} The list of practice categories
 */
const getPracticeCategories = async (
  practiceKey,
  perPage = 100,
  includeChildren = true,
  includeGrandchildren = true
) =>
  _getPracticeCategoryData(
    practiceKey,
    perPage,
    includeChildren,
    includeGrandchildren
  )

/**
 * Retrieves an object array of practice categories for the given practice type, using the
 * practice data provided as a reference
 * @param {Array} practices A WP REST API response object array containing practice data
 * @returns {Array} The list of practice categories
 */
const getCategoriesFromPractices = practices => {
  if (!practices?.length) return []

  let categories = []

  // Loop through the practices and build a list of unique categories
  practices.forEach(practice => {
    if (practice.categories) {
      practice.categories.forEach(category => {
        // Check if the category already exists in the list
        const existingIndex = categories.findIndex(
          item => item.id === category.id
        )

        if (existingIndex === -1) {
          // If it doesn't exist, add it to the list
          categories.push({
            ...category,
            count: 1
          })
        } else {
          // If it does exist, increment the count
          const current = categories[existingIndex]
          categories[existingIndex] = {
            ...current,
            count: current.count + 1
          }
        }
      })
    }
  })

  return categories
}

/**
 * Fetches a list of available affirmation assets of the specified type
 * @param {String} type The asset type to retrieve (e.g., 'backgrounds' or 'watermarks')
 */
const getAffirmationAssets = async (type = 'backgrounds') => {
  const url = `${REMBLE_API_URL}/practices/affirmations/assets?type=${type}`
  return await practicesGET(url)
}

/**
 * Facade for retrieving data for the affirmation practice with the given practiceCategoryId
 * @param {Number} practiceCategoryId The WP category ID that the practice belongs to. These are defined in the .env file
 */
const getAffirmationData = async practiceCategoryId =>
  _getPracticeData(KEY_PRACTICE_AFFIRMATIONS, practiceCategoryId)

/**
 * Facade for retrieving the practice list for Affirmations
 * Automatically filters out the "Today..." affirmation category
 */
const getAffirmationPractices = async () =>
  _getPracticeParentCategoryData(KEY_PRACTICE_AFFIRMATIONS).then(
    result => result.filter(item => item.term_id !== affirmationTodayCategoryId) // Filter out the "Today..." affirmation category
  )

/**
 * Retrieves the daily affirmation post
 */
const getDailyAffirmation = async () => {
  const uri = `/affirmation/category/${affirmationTodayCategoryId}`
  const url = buildRequestURL(PRACTICES_BASE_PATH, { uri })
  const affirmations = await practicesGET(url)

  // Return early if we don't have a result
  if (!affirmations || !affirmations.length) return

  // Grab the affirmation with today's post_date or a random one, if we don't have one for today
  const todaysAffirmation =
    affirmations.find(x => moment().isSame(x.post_date, 'day')) ||
    affirmations[Math.floor(Math.random() * affirmations.length)]

  return todaysAffirmation?.post_title
}

/**
 * Retrieves the start your day affirmations list
 */
const getDailyAffirmationsList = async () => {
  const uri = `/affirmation/category/${affirmationStartYourDayCategoryId}`
  const url = buildRequestURL(PRACTICES_BASE_PATH, { uri })
  const affirmations = await practicesGET(url)

  // Return early if we don't have a result
  if (!affirmations || !affirmations.length) return false
  else return affirmations.map(affirmation => affirmation.post_title)
}

/**
 * Facade for retrieving data for the Relationship Conversations practice with the given practiceCategoryId
 * @param {Number} practiceCategoryId The WP category ID that the practice belongs to. These are defined in the .env file
 */
const getRelationshipConversationData = async practiceCategoryId =>
  _getPracticeData(KEY_PRACTICE_RELATIONSHIPQA, practiceCategoryId)

/**
 * Facade for retrieving the practice list for Relationship Conversations
 * @param {Number} perPage The number of items to retrieve per page
 * @param {Boolean} includeChildren Whether or not to include child categories
 * @param {Boolean} includeGrandchildren Whether or not to include grandchild categories
 * @returns {Array} An array of practice categories
 */
const getRelationshipConversationPractices = async (
  perPage = 100,
  includeChildren = true,
  includeGrandchildren = true
) =>
  _getPracticeParentCategoryData(
    KEY_PRACTICE_RELATIONSHIPQA,
    perPage,
    includeChildren,
    includeGrandchildren
  )

/**
 * Facade for retrieving data for the Journaling practice with the given practiceCategoryId
 * @param {Number} practiceCategoryId The WP category ID that the practice belongs to. These are defined in the .env file
 */
const getJournalingData = async practiceCategoryId =>
  _getPracticeData(KEY_PRACTICE_JOURNALING, practiceCategoryId)

/**
 * Facade for retrieving the practice list for Journaling, filtering out the "Today..." journaling category and the "Free entry..." journaling category
 */
const getJournalingPractices = async () =>
  _getPracticeParentCategoryData(KEY_PRACTICE_JOURNALING).then(result =>
    result.filter(
      item =>
        item.term_id !== parseInt(journalingDailyCategoryId, 10) &&
        item.term_id !== parseInt(journalingFreeEntryCategoryId, 10)
    )
  )

/**
 * Facade for retrieving the practice list for Daily Journals, like "Today..." and the "Free entry..." journaling category
 */
const getDailyJournalData = async practiceId => {
  const url = `${WP_API_URL}/${KEY_PRACTICE_JOURNALING_WP}/${practiceId}`
  const practice = await practicesGET(url)
  return _normalizePracticeData(practice)
}

/**
 * Retrieves a list of all available journals for the current user
 */
const getUserJournals = async () => {
  const url = `${REMBLE_API_URL}/users/journals/find?limit=100`

  const result = await practicesGET(url)
  const journalsToShow = await getDailyJournals()
  //filter array by id equal to journalsToShow id
  return result.entries.filter(
    journal =>
      journalsToShow.includes(journal.journal_id) ||
      journal.journal_id === parseInt(journalingFreeEntryCategoryId, 10)
  )
}

/**
 * Retrives a journal by entry id
 * @param {Number} id Entry id
 */
const getUserJournalsEntriesByID = async id => {
  if (!id) return []
  const url = `${REMBLE_API_URL}/users/journals/find?entry_id=${id}` // ask Ron if this works
  const result = await practicesGET(url)
  return result.entries
}

/**
 * Retrives a journal by journal id
 * @param {Number} id Journal id
 */
const getUserJournalsByID = async id => {
  if (!id) return []
  const url = `${REMBLE_API_URL}/users/journals/find?journal_id=${id}`
  const result = await practicesGET(url)
  return [...result.entries] //The result may be an object or array, just to be save we always return an array
}

/**
 * Facade for retrieving the practice list for Daily Journals, like "Today..." and the "Free entry..." journaling category
 */
const getDailyJournals = async () => {
  const uri = `/${KEY_PRACTICE_JOURNALING}/category/${journalingDailyCategoryId}`
  const url = buildRequestURL(PRACTICES_BASE_PATH, { uri })
  const data = await practicesGET(url)
  return data.map(cleanDailyJournalingData)
}

/**
 * Creates a journal for the current user
 * @param {Object} params The journal data to create
 */
const createJournalEntry = async params => {
  if (params.previousId) {
    const url = `${REMBLE_API_URL}/users/journals/update`
    let dataToUpdate = {
      id: params.previousId,
      journal_text: params.journal_text,
      journal_emoji: params.journal_emoji
    }

    return await practicesPATCH(url, {
      body: JSON.stringify(dataToUpdate)
    }).catch(error => {
      console.error('error', error)
      return { error }
    })
  }

  const url = `${REMBLE_API_URL}/users/journals/create`

  return practicesPOST(url, {
    body: JSON.stringify(params)
  }).catch(error => {
    console.error('error', error)
    return { error }
  })
}

/**
 * Deletes a journal for the current user
 * @param {Number} journalId The journal data to be deleted
 */
const deleteJournalEntry = async journalId => {
  const url = `${REMBLE_API_URL}/users/journals/delete/${journalId}`
  return await practicesDELETE(url)
}

/**
 * Facade for retrieving data for the Meditation practice with the given practiceId
 * @param {Number} practiceId The WP post ID for the practice
 */
const getMeditationData = async practiceId => {
  const uri = `${WP_API_PUBLIC_URL}/${KEY_PRACTICE_MEDITATIONS_WP}/${practiceId}`
  return await practicesGET(WPJSON_BASE_PATH, { uri })
}

/**
 * Facade for retrieving the practice list for Meditations
 */
const getMeditationPractices = async () =>
  _getPracticesByType(KEY_PRACTICE_MEDITATIONS_WP)

/**
 * Facade for retrieving the practice list for Breathwork
 */
const getBreathingPractices = async () =>
  _getPracticesByType(KEY_PRACTICE_BREATHWORK_WP)

/**
 * Facade for retrieving data for the Breathwork practice with the given practiceId
 * @param {Number} practiceId The WP post ID for the practice
 * @returns {Promise} A promise that resolves to the practice data
 */
const getBreathingData = async practiceId => {
  const uri = `${WP_API_PUBLIC_URL}/${KEY_PRACTICE_BREATHWORK_WP}/${practiceId}`
  return await practicesGET(WPJSON_BASE_PATH, { uri })
}

/**
 * Facade for retrieving the practice data for a specific Coping Skill
 * @param {Number} practiceId The WP post ID for the practice
 * @returns {Promise} A promise that resolves to the practice data
 */
const getCopingSkillsData = async practiceId => {
  const uri = `${WP_API_PUBLIC_URL}/${KEY_PRACTICE_COPING_SKILLS_WP}/${practiceId}`
  return await practicesGET(WPJSON_BASE_PATH, { uri })
}

/**
 * Facade for retrieving the practice list for Coping Skills
 */
const getCopingSkillsPractices = async () =>
  _getPracticesByType(KEY_PRACTICE_COPING_SKILLS_WP)

/**
 * Facade for retrieving the practice list for Compliments
 */
const getComplimentsPractices = async () =>
  _getPracticeParentCategoryData(KEY_PRACTICE_COMPLIMENTS).then(result =>
    result.filter(item => item.term_id !== parseInt(complimentsCategoryId, 10))
  )

const getComplimentsData = async practiceCategoryId =>
  _getPracticeData(KEY_PRACTICE_COMPLIMENTS, practiceCategoryId)

/**
 * Facade for retrieving the practice list for Date Ideas
 */
const getDateIdeasPractices = async () =>
  _getPracticeParentCategoryData(KEY_PRACTICE_DATE_IDEAS).then(result =>
    result.filter(item => item.term_id !== parseInt(dateIdeasCategoryId, 10))
  )

const getDateIdeasData = async practiceCategoryId =>
  _getPracticeData(KEY_PRACTICE_DATE_IDEAS, practiceCategoryId)

const _practicesRESTGet = async (url, queryObj, paramObj) => {
  const requestUrl = buildRequestURL(url, queryObj)
  const params = getRESTParams(paramObj)
  const result = await fetch(requestUrl, params)
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  // JWT expired
  if (result?.code === ERR_CODE_JWT_OBSOLETE) throw new Error(ERR_JWT_OBSOLETE)

  return result
}

const getPracticesByCategory = async categoryId => {
  let practices = []

  const url = `${REMBLE_WP_API_URL}/practices/category/${categoryId}`
  practices = await _practicesRESTGet(url)

  return practices
}

export {
  getAllPracticesDetails,
  getPracticeDetails,
  getCategoriesFromPractices,
  getAffirmationAssets,
  getPracticeCategories,
  getBreathingPractices,
  getBreathingData,
  getJournalingPractices,
  getJournalingData,
  getDailyJournalData,
  getMeditationData,
  getMeditationPractices,
  getAffirmationPractices,
  getAffirmationData,
  getDailyAffirmation,
  getDailyAffirmationsList,
  getRelationshipConversationPractices,
  getRelationshipConversationData,
  getCopingSkillsPractices,
  getCopingSkillsData,
  getComplimentsPractices,
  getComplimentsData,
  getDateIdeasPractices,
  getDateIdeasData,
  getUserJournals,
  getUserJournalsByID,
  getUserJournalsEntriesByID,
  createJournalEntry,
  deleteJournalEntry,
  getDailyJournals,
  isFreePracticeCategory,
  getPracticesByCategory
}
